import {
  Badge,
  Banner,
  Button,
  ClipboardText,
  Collapsible,
  Dialog,
  Grid,
  GridItem,
  Loader,
  Surface,
  Table,
  Text,
  Textarea,
} from '@cloudflare/kumo'
import {
  ArrowLeft,
  ArrowsClockwise,
  CaretDown,
  ChatText,
  CheckCircle,
  FloppyDisk,
  PencilSimple,
  Plus,
  Trash,
  User,
} from '@phosphor-icons/react'
import {
  HEARD_FROM_LABELS,
  LANGUAGE_NAMES,
  SHELF_LIFE_COPY,
  productCategoryLabel,
  QUOTE_SLA_DAYS,
  type AdminQuoteDetail,
  type AdminQuoteView,
  type FactoryView,
  nextQuoteRequestStatuses,
  quoteRequestStatusOf,
  type QuoteOfferEventView,
  type QuoteRequestStatus,
  type QuoteResult,
  type QuoteStatusEventView,
} from '@repo/shared'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { adminApi } from '../api'
import { ConfirmDialog } from '../components/confirm-dialog'
import { MessageDialog } from '../components/message-dialog'
import { PaymentSourceBadge, QuoteCloseBadge } from '../components/payment-source-badge'
import { emptyResult, OfferForm } from '../components/offer-form'
import { factoriesApi } from '../factories-api'
import { useGetToken } from '../lib/auth'
import { useAdminAccess } from '../lib/admin-access'
import { fmtInt, fmtMoney, fmtTime, toMessage } from '../lib/format'
import {
  EVENT_BADGE,
  EVENT_LABEL,
  REQUEST_STATUS_LABEL,
  STATUS_ACTOR_LABEL,
  STATUS_BADGE,
  STATUS_LABEL,
  STORAGE_LABEL,
  sampleText,
} from '../lib/quote-labels'
import { refreshSummary } from '../lib/summary'
import { ManufacturingPanel } from '../components/manufacturing-panel'
import { QuoteFormulaBasisBanner } from '../components/quote-formula-basis'
import { useConfigSwitch, type AppConfigResponse } from '../lib/config-switch'

/** 관리자가 직접 고르는 새 단계 (Top10 #5). 마감·다시 열기는 예전 버튼이 맡는다 */
const STEP_STATUSES: readonly QuoteRequestStatus[] = ['reviewing', 'needs_information']

/** 새 단계(공장 확인·추가 정보 요청)가 켜져 있는지 — 서버 코드와 관리자 설정 두 겹 */
const quoteStepsOn = (c: AppConfigResponse) =>
  c.features?.quoteStatuses === true && c.settings.quoteStatuses.enabled

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <Text variant="secondary" size="xs">
        {label}
      </Text>
      <div className="min-w-0 break-words text-sm">{children}</div>
    </div>
  )
}

/** 내역의 "바뀐 내용" 에 쓰는 필드 이름 */
const FIELD_LABEL: Record<keyof QuoteResult, string> = {
  factoryId: '공장 목록 연결',
  factoryName: '공장명',
  factoryLocation: '소재지',
  factoryCertifications: '공장 인증',
  currency: '통화',
  unitPrice: '단가',
  moq: 'MOQ',
  casePack: '케이스 입수',
  orderUnit: '발주 단위',
  leadTimeDays: '납기',
  validUntil: '유효기한',
  suggestedRetail: '참고 판매가',
  paymentTerms: '결제 조건',
  barcode: '바코드',
  dimensionsMm: '치수',
  taxRatePct: '부가세율',
  description: '제품 설명',
  sample: '샘플',
  lines: '기타 비용',
  notes: '메모',
}

/** 수정 전후를 비교해 바뀐 필드를 "단가 1,450 → 1,500" 처럼. 숫자·짧은 값만 전후를 보여주고 나머지는 이름만 */
function offerChanges(before: QuoteResult | undefined, after: QuoteResult): string[] {
  if (!before) return []
  return (
    (Object.keys(FIELD_LABEL) as (keyof QuoteResult)[])
      // 칸이 생기기 전의 기록(sample 없음)과 null 은 같다고 본다
      .filter((k) => JSON.stringify(before[k] ?? null) !== JSON.stringify(after[k] ?? null))
      .map((k) => {
        const a = before[k]
        const b = after[k]
        if (k === 'sample') {
          return `${FIELD_LABEL[k]} ${sampleText(before.sample, before.currency)} → ${sampleText(after.sample, after.currency)}`
        }
        const short = (v: unknown) =>
          typeof v === 'number'
            ? fmtInt(v)
            : typeof v === 'string' && v.length <= 20
              ? v || '없음'
              : null
        const from = short(a)
        const to = short(b)
        return from !== null && to !== null
          ? `${FIELD_LABEL[k]} ${from} → ${to}`
          : `${FIELD_LABEL[k]} 변경`
      })
  )
}

/** 이력의 상태 이름. 요청 상태는 한국어로, 제안 상태(accepted 등)·모름(null)은 그대로 */
function statusText(status: string | null): string {
  if (status === null) return '—'
  if (status === 'accepted') return '선택됨'
  return REQUEST_STATUS_LABEL[status as QuoteRequestStatus] ?? status
}

/**
 * 견적 요청 상세 — 요청자 · 스펙시트 · 운영 메모 → 공장 견적 입력(여러 건, 저장하면 사용자에게 푸시) →
 * 사용자가 공장 선택 → 연결 후 마감. 견적의 입력·수정·삭제는 전부 [견적 내역]에 쌓인다.
 */
export function QuoteDetailPage() {
  const getToken = useGetToken()
  const navigate = useNavigate()
  const { id = '' } = useParams()
  const [quote, setQuote] = useState<AdminQuoteView | null>(null)
  const [history, setHistory] = useState<QuoteOfferEventView[]>([])
  const [statusHistory, setStatusHistory] = useState<QuoteStatusEventView[]>([])
  const stepsOn = useConfigSwitch(quoteStepsOn)
  const [form, setForm] = useState<QuoteResult | null>(null)
  /** 수정 중인 견적 id (null = 새 견적) */
  const [editingOfferId, setEditingOfferId] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [manufacturingEnabled, setManufacturingEnabled] = useState(true)
  const [confirm, setConfirm] = useState<{ kind: 'close' } | { kind: 'offer'; id: string } | null>(
    null,
  )
  const [messageOpen, setMessageOpen] = useState(false)
  const [closeReason, setCloseReason] = useState('')
  // 배정 운영자는 사용자 카드·시뮬레이션·마감(견적권 반환)을 쓸 수 없다 (서버 OPERATOR_ROUTES, C52)
  const owner = useAdminAccess().level === 'owner'
  /** 견적 폼의 공장 고르기 목록. 못 읽으면(배정 운영자는 공장 목록 권한이 없다) 빈 목록 — 손으로 적는 옛 방식은 그대로 된다 */
  const [factories, setFactories] = useState<FactoryView[]>([])

  useEffect(() => {
    factoriesApi
      .list(getToken)
      .then((r) => setFactories(r.items))
      .catch(() => setFactories([]))
  }, [getToken])

  const applyDetail = (d: AdminQuoteDetail) => {
    setQuote(d.quote)
    setHistory(d.history)
    setStatusHistory(d.statusHistory ?? [])
    // 상태가 바뀌었을 수 있다 → 사이드바 배지를 바로 갱신
    refreshSummary()
  }

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(toMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const reload = useCallback(async () => {
    setBusy(true)
    try {
      const d = await adminApi.quotes.get(getToken, id)
      setQuote(d.quote)
      setHistory(d.history)
      setStatusHistory(d.statusHistory ?? [])
      setError(null)
      refreshSummary()
      return d
    } catch (err) {
      setError(toMessage(err))
      return null
    } finally {
      setBusy(false)
    }
  }, [getToken, id])

  // 다른 요청으로 옮기면 폼·메모를 그 요청 것으로
  useEffect(() => {
    setQuote(null)
    setHistory([])
    setStatusHistory([])
    setForm(null)
    setEditingOfferId(null)
    setNotice(null)
    void reload().then((d) => setNotes(d?.quote.adminNotes ?? ''))
  }, [reload])

  const startNew = () => {
    if (!quote) return
    setEditingOfferId(null)
    setForm(emptyResult(quote))
  }
  const startEdit = (offerId: string) => {
    const o = quote?.offers.find((x) => x.id === offerId)
    if (!o) return
    const { id: _id, createdAt: _c, ...rest } = o
    setEditingOfferId(offerId)
    setForm(rest)
  }

  const save = () =>
    run(async () => {
      if (!quote || !form) return
      applyDetail(
        editingOfferId
          ? await adminApi.quotes.updateOffer(getToken, quote.id, editingOfferId, form)
          : await adminApi.quotes.addOffer(getToken, quote.id, form),
      )
      setNotice(
        editingOfferId ? '견적을 수정했어요.' : '견적을 보냈어요. 사용자에게 푸시가 갔어요.',
      )
      setForm(null)
      setEditingOfferId(null)
    })

  const removeOffer = (offerId: string) =>
    run(async () => {
      if (!quote) return
      applyDetail(await adminApi.quotes.removeOffer(getToken, quote.id, offerId))
      if (editingOfferId === offerId) {
        setForm(null)
        setEditingOfferId(null)
      }
      setConfirm(null)
      setNotice('견적을 삭제했어요. 내역에는 남아 있어요.')
    })

  const close = () =>
    run(async () => {
      if (!quote) return
      const res = await adminApi.quotes.setStatus(getToken, quote.id, 'closed', closeReason)
      setQuote(res.quote)
      refreshSummary()
      setConfirm(null)
      setCloseReason('')
      setNotice('마감했어요. 사용자에게 알림이 갔어요.')
    })

  const moveTo = (to: QuoteRequestStatus) =>
    run(async () => {
      if (!quote) return
      await adminApi.quotes.setStatus(getToken, quote.id, to)
      // 상태 이력도 새로 읽는다
      await reload()
      setNotice(
        to === 'needs_information'
          ? `'${REQUEST_STATUS_LABEL[to]}'로 바꿨어요. 무엇이 더 필요한지 '요청자에게 메시지'로 알려 주세요.`
          : `'${REQUEST_STATUS_LABEL[to]}'로 바꿨어요.`,
      )
    })

  const saveNotes = () =>
    run(async () => {
      if (!quote) return
      const res = await adminApi.quotes.setNotes(getToken, quote.id, notes)
      setQuote(res.quote)
      setNotice('메모를 저장했어요.')
    })

  const sendMessage = (msg: { title: string; body: string }) =>
    run(async () => {
      if (!quote) return
      await adminApi.quotes.message(getToken, quote.id, msg)
      setMessageOpen(false)
      setNotice('메시지를 보냈어요.')
    })

  const chosen = quote?.offers.find((o) => o.id === quote.selectedOfferId) ?? null
  const requestStatus = quote ? quoteRequestStatusOf(quote) : null
  const stepButtons =
    stepsOn && requestStatus && !manufacturingEnabled
      ? nextQuoteRequestStatuses(requestStatus, 'admin').filter((s) => STEP_STATUSES.includes(s))
      : []

  const back = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <Button variant="ghost" icon={<ArrowLeft />} onClick={() => navigate('/quotes')}>
        견적 목록
      </Button>
      <Button
        variant="secondary"
        icon={<ArrowsClockwise />}
        loading={busy}
        onClick={() => void reload()}
      >
        새로고침
      </Button>
    </div>
  )

  if (!quote) {
    return (
      <div className="flex flex-col gap-6">
        {back}
        {error ? (
          <Banner variant="error" title="견적 요청을 불러오지 못했어요" description={error} />
        ) : (
          <div className="flex items-center gap-3 p-6">
            <Loader />
            <Text variant="secondary">불러오는 중…</Text>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {back}
      {error && <Banner variant="error" title="문제가 생겼어요" description={error} />}

      <Surface className="flex flex-col gap-6 rounded-lg p-6" data-testid="quote-detail">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <Text variant="heading" size="lg">
                {quote.request.productName}
              </Text>
              <Badge variant={STATUS_BADGE[quote.status]}>{STATUS_LABEL[quote.status]}</Badge>
              {stepsOn && requestStatus && STEP_STATUSES.includes(requestStatus) && (
                <Badge variant="info">{REQUEST_STATUS_LABEL[requestStatus]}</Badge>
              )}
              {quote.overdue && <Badge variant="error">지연</Badge>}
              <PaymentSourceBadge source={quote.paymentSource} />
              <QuoteCloseBadge quote={quote} />
            </div>
            <Text variant="secondary" size="sm">
              {productCategoryLabel(quote.request.categoryId)} · {quote.request.format} ·{' '}
              {quote.request.netWeight.amount}
              {quote.request.netWeight.unit} · {STORAGE_LABEL[quote.request.storage]} ·{' '}
              {SHELF_LIFE_COPY.ko.label} · 접수 {fmtTime(quote.createdAt)}
            </Text>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              icon={<ChatText />}
              disabled={busy}
              onClick={() => setMessageOpen(true)}
            >
              요청자에게 메시지
            </Button>
            {owner && (
              <Button
                variant="secondary"
                icon={<User />}
                onClick={() => navigate(`/users?id=${encodeURIComponent(quote.userId)}`)}
              >
                사용자 카드
              </Button>
            )}
            {stepButtons.map((to) => (
              <Button key={to} variant="secondary" disabled={busy} onClick={() => moveTo(to)}>
                {REQUEST_STATUS_LABEL[to]}
              </Button>
            ))}
            {owner && !manufacturingEnabled && quote.status !== 'closed' && (
              <Button
                variant="secondary"
                icon={<CheckCircle />}
                disabled={busy}
                onClick={() => setConfirm({ kind: 'close' })}
              >
                마감
              </Button>
            )}
          </div>
        </div>

        <ManufacturingPanel key={quote.id} quoteId={quote.id} onEnabled={setManufacturingEnabled} />

        {/* 지금 할 일 */}
        {!manufacturingEnabled && quote.status === 'submitted' && (
          <Banner
            variant={quote.overdue ? 'error' : 'alert'}
            title={quote.overdue ? '견적이 늦어지고 있어요' : '다음: 공장 견적 입력'}
            description={`스펙시트를 공장에 전달하고, 답이 오면 아래 "공장 견적 추가"로 입력하세요. 접수 후 ${QUOTE_SLA_DAYS}일 안이 목표예요.`}
          />
        )}
        {!manufacturingEnabled && quote.status === 'quoted' && (
          <Banner
            variant="default"
            title="사용자가 공장을 고르기를 기다리는 중"
            description="다른 공장 견적이 오면 더 추가할 수 있어요. 오래 답이 없으면 메시지를 보내 보세요."
          />
        )}
        {!manufacturingEnabled && quote.status === 'accepted' && (
          <Banner
            variant="default"
            title={`다음: ${chosen?.factoryName || '선택된 공장'} 과 사용자 연결`}
            description="사용자가 공장을 골랐어요. 아래 요청자 연락처로 공장과 연결(샘플·발주)한 뒤 마감하세요."
          />
        )}
        {quote.status === 'closed' && (
          <Banner
            variant="default"
            title="마감된 견적이에요"
            description={`사용자는 이제 공장을 고르거나 바꿀 수 없어요.${
              chosen
                ? ` 최종 선택: ${chosen.factoryName || '이름 없는 공장'}`
                : ' 고른 공장 없이 마감됐어요.'
            }`}
          />
        )}
        {notice && <Banner variant="default" title={notice} />}

        <Grid variant="3up" gap="sm">
          <GridItem>
            <Fact label="요청자">
              {quote.request.contact.name || '—'}
              {quote.request.contact.email && (
                <ClipboardText
                  text={quote.request.contact.email}
                  className="mt-1 font-mono text-xs"
                />
              )}
            </Fact>
          </GridItem>
          <GridItem>
            <Fact label="희망 수량 · 목표 단가">
              {fmtInt(quote.request.quantity)}개 ·{' '}
              {quote.request.targetUnitPrice === null
                ? '목표가 없음'
                : fmtMoney(quote.request.targetUnitPrice, quote.request.currency)}
            </Fact>
          </GridItem>
          <GridItem>
            <Fact label="요청 언어">
              {LANGUAGE_NAMES[quote.request.language]} ({quote.request.language})
            </Fact>
          </GridItem>
          <GridItem>
            <Fact label="포장">
              {[quote.request.container, quote.request.lid, quote.request.packagingNotes]
                .filter(Boolean)
                .join(' · ') || '—'}
            </Fact>
          </GridItem>
          <GridItem>
            <Fact label="요구 사항">
              {[
                quote.request.cleanLabel ? '클린 라벨' : null,
                quote.request.gmoFree ? 'Non-GMO' : null,
                quote.request.substitutesOk ? '원료 대체 허용' : '원료 대체 불가',
                ...quote.request.certifications,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Fact>
          </GridItem>
          <GridItem>
            <Fact label="사용자 메모">{quote.request.notes || '—'}</Fact>
          </GridItem>
          <GridItem>
            {/* 선택 항목 (C56). 옛 앱·옛 요청·질문을 끈 동안의 요청에는 없다 */}
            <Fact label="어떻게 알고 왔나">
              {quote.request.heardFrom ? HEARD_FROM_LABELS.ko[quote.request.heardFrom] : '답 없음'}
            </Fact>
          </GridItem>
        </Grid>

        <QuoteFormulaBasisBanner basis={quote.formulaBasis} currency={quote.request.currency} />

        {/* 스펙시트 */}
        <Collapsible.Root defaultOpen>
          <Collapsible.Trigger
            render={<Button variant="ghost" className="w-full justify-between px-0" />}
          >
            <Text variant="heading">스펙시트 (공장 전달용)</Text>
            <CaretDown size={16} />
          </Collapsible.Trigger>
          <Collapsible.Panel>
            <div className="mt-3 flex flex-col gap-4">
              <div className="border-kumo-line overflow-x-auto rounded-lg border">
                <Table>
                  <Table.Header variant="compact">
                    <Table.Row>
                      <Table.Head>원료</Table.Head>
                      <Table.Head>수량</Table.Head>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {quote.request.ingredients.map((i) => (
                      <Table.Row key={i.name}>
                        <Table.Cell>{i.name}</Table.Cell>
                        <Table.Cell className="tabular-nums">
                          {i.qty} {i.unit}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
              <Grid variant="3up" gap="sm">
                <GridItem>
                  <Fact label="총 배합 중량">
                    {quote.request.totalRecipeWeightG !== null
                      ? `${fmtInt(quote.request.totalRecipeWeightG)} g`
                      : '—'}
                  </Fact>
                </GridItem>
                <GridItem>
                  <Fact label="알레르겐">{quote.request.allergens.join(', ') || '없음'}</Fact>
                </GridItem>
                <GridItem>
                  <Fact label="필요 장비">{quote.request.equipment.join(', ') || '—'}</Fact>
                </GridItem>
              </Grid>
              <Fact label="공정">
                <ol className="list-decimal space-y-0.5 pl-5">
                  {quote.request.steps.map((st) => (
                    <li key={st.title}>
                      {[st.title, st.temp, st.time].filter(Boolean).join(' · ')}
                    </li>
                  ))}
                </ol>
              </Fact>
              <Fact label="제품 설명">{quote.request.productDescription || '—'}</Fact>
              <Fact label="시뮬레이션">
                {owner ? (
                  <button
                    type="button"
                    className="text-kumo-link font-mono text-xs hover:underline"
                    onClick={() => navigate(`/simulations?open=${quote.simulationId}`)}
                  >
                    {quote.simulationId}
                  </button>
                ) : (
                  <span className="font-mono text-xs">{quote.simulationId}</span>
                )}
              </Fact>
            </div>
          </Collapsible.Panel>
        </Collapsible.Root>

        {/* 운영 메모 */}
        <div className="flex flex-col gap-2">
          <Textarea
            label="운영 메모"
            description="사용자에게는 보이지 않아요. 공장 연락 상황, 샘플 일정 등을 남기세요."
            value={notes}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
          />
          <div>
            <Button
              variant="secondary"
              icon={<FloppyDisk />}
              disabled={busy || notes === quote.adminNotes}
              onClick={() => void saveNotes()}
            >
              메모 저장
            </Button>
          </div>
        </div>

        {/* 공장 견적 */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Text variant="heading">공장 견적 {quote.offers.length}건</Text>
            {/* 마감된 요청에는 서버가 견적을 더하지 않는다 (409) */}
            {!manufacturingEnabled && quote.status !== 'closed' && (
              <Button
                variant="primary"
                icon={<Plus />}
                disabled={busy || form !== null}
                onClick={startNew}
              >
                공장 견적 추가
              </Button>
            )}
          </div>
          {quote.offers.length === 0 && !form && (
            <Text variant="secondary" size="sm">
              아직 입력한 견적이 없어요.
            </Text>
          )}
          {quote.offers.length > 0 && (
            <div className="border-kumo-line overflow-x-auto rounded-lg border">
              <Table>
                <Table.Header variant="compact">
                  <Table.Row>
                    <Table.Head>공장</Table.Head>
                    <Table.Head>단가</Table.Head>
                    <Table.Head>MOQ</Table.Head>
                    <Table.Head>납기</Table.Head>
                    <Table.Head>샘플</Table.Head>
                    <Table.Head>입력</Table.Head>
                    <Table.Head>
                      <span className="sr-only">작업</span>
                    </Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {quote.offers.map((o) => (
                    <Table.Row key={o.id} className={editingOfferId === o.id ? 'bg-kumo-tint' : ''}>
                      <Table.Cell>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{o.factoryName || '—'}</span>
                          {quote.selectedOfferId === o.id && (
                            <Badge variant="success">사용자가 선택</Badge>
                          )}
                        </div>
                        <div className="text-kumo-subtle text-xs">{o.factoryLocation}</div>
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap tabular-nums">
                        {fmtMoney(o.unitPrice, o.currency)}
                      </Table.Cell>
                      <Table.Cell className="tabular-nums">{fmtInt(o.moq)}</Table.Cell>
                      <Table.Cell className="whitespace-nowrap">{o.leadTimeDays}일</Table.Cell>
                      <Table.Cell className="whitespace-nowrap text-xs">
                        {sampleText(o.sample, o.currency)}
                      </Table.Cell>
                      <Table.Cell className="whitespace-nowrap text-xs">
                        {fmtTime(o.createdAt)}
                      </Table.Cell>
                      <Table.Cell>
                        {/* 고객이 고른 견적과 마감된 요청의 견적은 서버가 막는다 (409) */}
                        {manufacturingEnabled ||
                        quote.status === 'closed' ||
                        quote.selectedOfferId === o.id ? (
                          <div className="text-kumo-subtle text-right text-xs">잠김</div>
                        ) : (
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<PencilSimple />}
                              disabled={busy}
                              onClick={() => startEdit(o.id)}
                            >
                              수정
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<Trash />}
                              disabled={busy}
                              onClick={() => setConfirm({ kind: 'offer', id: o.id })}
                            >
                              삭제
                            </Button>
                          </div>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}

          {form && (
            <OfferForm
              key={editingOfferId ?? 'new'}
              form={form}
              setForm={setForm}
              editing={editingOfferId !== null}
              language={LANGUAGE_NAMES[quote.request.language]}
              factories={factories}
              request={quote.request}
              busy={busy}
              onSave={() => void save()}
              onCancel={() => {
                setForm(null)
                setEditingOfferId(null)
              }}
            />
          )}
        </div>
        {/* 상태 이력 (명세 상태, 최신순). 이 칸이 생기기 전의 변경은 없다 */}
        <div className="flex flex-col gap-3" data-testid="status-history">
          <Text variant="heading">상태 이력 {statusHistory.length}건</Text>
          {statusHistory.length === 0 ? (
            <Text variant="secondary" size="sm">
              아직 기록이 없어요.
            </Text>
          ) : (
            <div className="border-kumo-line overflow-x-auto rounded-lg border">
              <Table>
                <Table.Header variant="compact">
                  <Table.Row>
                    <Table.Head>시각</Table.Head>
                    <Table.Head>바뀐 상태</Table.Head>
                    <Table.Head>누가</Table.Head>
                    <Table.Head>이유</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {statusHistory.map((e) => (
                    <Table.Row key={e.id}>
                      <Table.Cell className="whitespace-nowrap text-xs">
                        {fmtTime(e.createdAt)}
                      </Table.Cell>
                      <Table.Cell className="text-xs">
                        {e.scope === 'offer' ? '공장 선택: ' : ''}
                        {statusText(e.from)} → {statusText(e.to)}
                      </Table.Cell>
                      <Table.Cell className="text-xs">{STATUS_ACTOR_LABEL[e.actor]}</Table.Cell>
                      <Table.Cell className="text-xs">{e.reason || '—'}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>

        {/* 견적 내역 */}
        <div className="flex flex-col gap-3" data-testid="offer-history">
          <div>
            <Text variant="heading">견적 내역 {history.length}건</Text>
            <Text variant="secondary" size="sm">
              공장 견적을 입력·수정·삭제할 때마다 그 시점의 값과 작업자가 남아요. 지운 견적도
              여기에는 남아요.
            </Text>
          </div>
          {history.length === 0 ? (
            <Text variant="secondary" size="sm">
              아직 기록이 없어요.
            </Text>
          ) : (
            <div className="border-kumo-line overflow-x-auto rounded-lg border">
              <Table>
                <Table.Header variant="compact">
                  <Table.Row>
                    <Table.Head>시각</Table.Head>
                    <Table.Head>작업</Table.Head>
                    <Table.Head>공장</Table.Head>
                    <Table.Head>단가</Table.Head>
                    <Table.Head>MOQ</Table.Head>
                    <Table.Head>납기</Table.Head>
                    <Table.Head>바뀐 내용</Table.Head>
                    <Table.Head>작업자</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {history.map((e, i) => (
                    <Table.Row key={e.id} data-testid="offer-history-row">
                      <Table.Cell className="whitespace-nowrap text-xs">
                        {fmtTime(e.createdAt)}
                      </Table.Cell>
                      <Table.Cell>
                        <Badge variant={EVENT_BADGE[e.kind]}>{EVENT_LABEL[e.kind]}</Badge>
                      </Table.Cell>
                      <Table.Cell className="font-medium">{e.offer.factoryName || '—'}</Table.Cell>
                      <Table.Cell className="whitespace-nowrap tabular-nums">
                        {fmtMoney(e.offer.unitPrice, e.offer.currency)}
                      </Table.Cell>
                      <Table.Cell className="tabular-nums">{fmtInt(e.offer.moq)}</Table.Cell>
                      <Table.Cell className="whitespace-nowrap">
                        {e.offer.leadTimeDays}일
                      </Table.Cell>
                      <Table.Cell className="text-xs">
                        {e.kind === 'updated'
                          ? offerChanges(
                              history.slice(i + 1).find((p) => p.offerId === e.offerId)?.offer,
                              e.offer,
                            ).join(' · ') || '값 변화 없음'
                          : '—'}
                      </Table.Cell>
                      <Table.Cell className="text-xs">{e.adminEmail || '—'}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
        </div>
      </Surface>

      {/* 확인 창에 이유 칸이 있어야 해서 ConfirmDialog 대신 같은 모양의 창을 쓴다 */}
      <Dialog.Root
        role="alertdialog"
        open={confirm?.kind === 'close'}
        onOpenChange={(o) => !o && setConfirm(null)}
      >
        <Dialog size="sm" className="p-6">
          <Dialog.Title className="text-lg font-semibold">이 요청을 마감할까요?</Dialog.Title>
          <Dialog.Description className="text-kumo-subtle mb-4 mt-2">
            사용자에게 '마감' 알림이 가요. 공장 연결까지 끝났을 때 마감하세요.
          </Dialog.Description>
          <Textarea
            label="마감 이유 (선택, 작업 기록에만 남아요)"
            maxLength={500}
            value={closeReason}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCloseReason(e.target.value)}
          />
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" disabled={busy} onClick={() => setConfirm(null)}>
              취소
            </Button>
            <Button variant="primary" loading={busy} onClick={() => void close()}>
              마감
            </Button>
          </div>
        </Dialog>
      </Dialog.Root>
      <ConfirmDialog
        open={confirm?.kind === 'offer'}
        title="이 공장 견적을 삭제할까요?"
        description="사용자 화면에서 사라져요(어드민의 견적 내역에는 남아요). 사용자가 이미 고른 견적이면 선택이 풀리고 '견적 보냄'으로 돌아가요."
        confirmLabel="삭제"
        destructive
        busy={busy}
        onConfirm={() => confirm?.kind === 'offer' && void removeOffer(confirm.id)}
        onClose={() => setConfirm(null)}
      />
      <MessageDialog
        open={messageOpen}
        heading="요청자에게 메시지"
        description="앱의 알림 탭에 남고 푸시가 가요. 누르면 이 견적 화면이 열려요."
        defaultTitle={`${quote.request.productName} 견적 안내`}
        busy={busy}
        onSend={(msg) => void sendMessage(msg)}
        onClose={() => setMessageOpen(false)}
      />
    </div>
  )
}
